import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth.jsx';
import LoginPage from './pages/LoginPage.jsx';
import ChangePasswordPage from './pages/ChangePasswordPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import AdminDashboardPage from './pages/AdminDashboardPage.jsx';
import AdminEmployeesPage from './pages/AdminEmployeesPage.jsx';
import AdminAddEmployeePage from './pages/AdminAddEmployeePage.jsx';
import AdminEmployeeDetailPage from './pages/AdminEmployeeDetailPage.jsx';
import AdminPresencePage from './pages/AdminPresencePage.jsx';
import AdminLeaveQueuePage from './pages/AdminLeaveQueuePage.jsx';
import AdminHolidaysPage from './pages/AdminHolidaysPage.jsx';
import LeavePage from './pages/LeavePage.jsx';
import MyScreenshotsPage from './pages/MyScreenshotsPage.jsx';
import { AdminLayout } from './components/AdminLayout.jsx';

function RequireAuth({ children, role }) {
    const { user, bootstrapping } = useAuth();
    const location = useLocation();
    if (bootstrapping) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                Loading…
            </div>
        );
    }
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    // If the user is required to change their password, send them there
    // regardless of where they tried to go. The change-password route
    // itself is exempt (handled by its own route, which uses RequireAuthForPasswordChange).
    if (user.must_change_password) {
        return <Navigate to="/change-password" replace />;
    }
    if (role && user.role !== role) {
        // Wrong role — send them to their actual home
        const home = user.role === 'admin' ? '/admin' : '/';
        return <Navigate to={home} replace />;
    }
    return children;
}

// Like RequireAuth but does NOT redirect away when must_change_password=true.
// Used for the /change-password route itself.
function RequireAuthAllowChangePassword({ children }) {
    const { user, bootstrapping } = useAuth();
    const location = useLocation();
    if (bootstrapping) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                Loading…
            </div>
        );
    }
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return children;
}

function RedirectIfAuthed({ children }) {
    const { user, bootstrapping } = useAuth();
    if (bootstrapping) return null;
    if (user) {
        // Mid-password-reset users go to /change-password first
        if (user.must_change_password) {
            return <Navigate to="/change-password" replace />;
        }
        // Otherwise, send to role-appropriate home
        const home = user.role === 'admin' ? '/admin' : '/';
        return <Navigate to={home} replace />;
    }
    return children;
}

function HomeRedirect() {
    const { user } = useAuth();
    return <Navigate to={user?.role === 'admin' ? '/admin' : '/employee'} replace />;
}

export default function App() {
    return (
        <Routes>
            <Route
                path="/login"
                element={
                    <RedirectIfAuthed>
                        <LoginPage />
                    </RedirectIfAuthed>
                }
            />

            <Route
                path="/change-password"
                element={
                    <RequireAuthAllowChangePassword>
                        <ChangePasswordPage />
                    </RequireAuthAllowChangePassword>
                }
            />

            {/* Admin area */}
            <Route
                path="/admin"
                element={
                    <RequireAuth role="admin">
                        <AdminLayout />
                    </RequireAuth>
                }
            >
                <Route index element={<AdminDashboardPage />} />
                <Route path="presence" element={<AdminPresencePage />} />
                <Route path="employees" element={<AdminEmployeesPage />} />
                <Route path="employees/new" element={<AdminAddEmployeePage />} />
                <Route path="employees/:id" element={<AdminEmployeeDetailPage />} />
                <Route path="leave" element={<AdminLeaveQueuePage />} />
                <Route path="holidays" element={<AdminHolidaysPage />} />
            </Route>

            {/* Employee dashboard */}
            <Route
                path="/"
                element={
                    <RequireAuth>
                        <RoleAwareHome />
                    </RequireAuth>
                }
            />
            <Route
                path="/employee"
                element={
                    <RequireAuth>
                        <DashboardPage />
                    </RequireAuth>
                }
            />
            <Route
                path="/leave"
                element={
                    <RequireAuth>
                        <LeavePage />
                    </RequireAuth>
                }
            />
            <Route
                path="/my-screenshots"
                element={
                    <RequireAuth>
                        <MyScreenshotsPage />
                    </RequireAuth>
                }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function RoleAwareHome() {
    const { user } = useAuth();
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    return <DashboardPage />;
}
